// DOM-Helfer.
//
// Wichtig: `el()` setzt Text ausschließlich über Textknoten. Früher wurden
// Titel per innerHTML zusammengebaut – mit Nutzernamen und OCR-Text aus
// Kassenbons darin, also mit fremdem Inhalt in einer HTML-Senke.
// Hier gibt es diese Senke nicht mehr.

type Child = Node | string | number | null | undefined | false;

export interface ElAttrs {
  class?: string;
  id?: string;
  type?: string;
  value?: string | number;
  placeholder?: string;
  title?: string;
  href?: string;
  src?: string;
  alt?: string;
  role?: string;
  tabindex?: string | number;
  disabled?: boolean;
  checked?: boolean;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  rows?: string | number;
  maxlength?: string | number;
  name?: string;
  inputmode?: string;
  enterkeyhint?: string;
  autocomplete?: string;
  accept?: string;
  capture?: string;
  hidden?: boolean;
  style?: string;
  loading?: string;
  draggable?: boolean;
  open?: boolean;
  target?: string;
  rel?: string;
  list?: string;
  muted?: string;
  playsinline?: string;
  spellcheck?: string;
  autocapitalize?: string;
  [key: `data-${string}`]: string | number | boolean | undefined;
  [key: `aria-${string}`]: string | number | boolean | undefined;
  [key: `on${string}`]: EventListener | undefined;
}

const BOOLEAN_PROPS = new Set(['disabled', 'checked', 'hidden', 'draggable', 'open']);

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: ElAttrs = {},
  children: Child | Child[] = [],
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);

  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null || value === false) continue;

    if (key === 'class') {
      node.className = String(value);
    } else if (key.startsWith('on') && typeof value === 'function') {
      node.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (BOOLEAN_PROPS.has(key)) {
      (node as unknown as Record<string, unknown>)[key] = Boolean(value);
    } else if (key === 'value') {
      (node as unknown as { value: string }).value = String(value);
    } else {
      node.setAttribute(key, String(value));
    }
  }

  append(node, children);
  return node;
}

export function append(parent: Node, children: Child | Child[]): void {
  for (const child of Array.isArray(children) ? children : [children]) {
    if (child === null || child === undefined || child === false) continue;
    parent.appendChild(typeof child === 'object' ? child : document.createTextNode(String(child)));
  }
}

/** Kind-Knoten ersetzen (statt innerHTML = ''). */
export function replace(parent: Element, children: Child | Child[]): void {
  parent.replaceChildren();
  append(parent, children);
}

export function clear(parent: Element): void {
  parent.replaceChildren();
}

export function $<T extends Element = HTMLElement>(selector: string, root: ParentNode = document) {
  return root.querySelector<T>(selector);
}

export function $$<T extends Element = HTMLElement>(selector: string, root: ParentNode = document) {
  return [...root.querySelectorAll<T>(selector)];
}

/** Kurzform für einen Textabsatz mit Klasse. */
export function text(cls: string, content: string): HTMLParagraphElement {
  return el('p', { class: cls }, content);
}

/** Kleines sanftes Vibrieren – nur, wenn das Gerät es kann. */
export function haptic(pattern: number | number[] = 12): void {
  if (typeof navigator.vibrate === 'function') navigator.vibrate(pattern);
}

/**
 * FLIP-Animation: Positionen vor der Änderung merken, danach die Differenz
 * zurückanimieren. Damit „rutschen" Listeneinträge, statt zu springen.
 */
export function animateReorder(container: Element, mutate: () => void): void {
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) {
    mutate();
    return;
  }

  const before = new Map<Element, DOMRect>();
  for (const child of container.children) before.set(child, child.getBoundingClientRect());

  mutate();

  for (const child of container.children) {
    const from = before.get(child);
    if (!from) continue;
    const to = child.getBoundingClientRect();
    const dy = from.top - to.top;
    if (Math.abs(dy) < 1) continue;
    child.animate([{ transform: `translateY(${dy}px)` }, { transform: 'translateY(0)' }], {
      duration: 220,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
    });
  }
}
